import { useEffect, useMemo, useState, type ReactNode } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useListGeoSchemesQuery } from '@shared/api';
import {
  apiErrorMessage,
  fmtDateTime,
  isSchemeStatus,
  isWorkType,
  SCHEME_STATUSES,
  WORK_TYPES,
  workTypeShort,
  type GeoSchemeListItem,
  type GeoSchemeStatus,
} from '@entities/geodesy';
import { useActiveProjectContext } from '@shared/lib/activeProjectContext';
import { useDebouncedValue } from '@shared/lib/useDebouncedValue';
import { Button } from '@shared/ui/Button';
import { Input, Select } from '@shared/ui/Input';
import { useGeoObjectNames } from './hooks';
import { SchemeStatusBadge } from './parts';
import { DataTable, ErrorText, FieldLabel, Muted, Num, Row, TableWrap, ToneRow } from './ui';

const PAGE_SIZE = 30;

const Toolbar = styled.div`
  display: grid;
  grid-template-columns: minmax(190px, 1.3fr) minmax(160px, 0.9fr) minmax(130px, 0.7fr) minmax(180px, 1fr) auto;
  align-items: end;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};

  @media (max-width: 1100px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const ActionsSlot = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;

  @media (max-width: 960px) {
    grid-column: 1 / -1;
    justify-content: flex-start;
  }
`;

const NumberLink = styled(Link)`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  white-space: nowrap;
`;

const TitleCell = styled.div`
  min-width: 180px;
  font-weight: 500;
  text-wrap: pretty;
`;

/** Объект — не уже 150 px: иначе «ДГП-2, БОВ 40 000 м³/ч» рвётся на четыре строки. */
const ObjectCell = styled.div`
  min-width: 140px;
  max-width: 240px;
  text-wrap: pretty;
`;

const Attention = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.status.danger};
`;

const Reason = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1)};
  max-width: 260px;
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ClickRow = styled(ToneRow)`
  cursor: pointer;
`;

const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

interface Props {
  /** Ссылка строки: `${linkBase}/${id}`. */
  linkBase: string;
  /** Чья это витрина: геодезисту подсвечиваем отклонённые, инженеру — ждущие проверки. */
  audience: 'geodesist' | 'engineer';
  /** Статус фильтра по умолчанию (без параметра в URL). */
  defaultStatus?: GeoSchemeStatus;
  /** Активный объект из сквозного контекста. */
  activeProjectId?: string;
  /** Кнопки справа от фильтров («Новая ИС», «Запросить схему»). */
  actions?: ReactNode;
  /** Текст пустого реестра. */
  emptyText: ReactNode;
}

/**
 * Реестр исполнительных схем с фильтрами (статус, тип работ, поиск) — общий для
 * ОСНОВА.Геодезист и вкладки ПТО. Фильтры живут в URL: «назад» из карточки
 * возвращает тот же срез.
 */
export function SchemeRegistry({
  linkBase,
  audience,
  defaultStatus,
  activeProjectId: propActiveProjectId,
  actions,
  emptyText,
}: Props) {
  const navigate = useNavigate();
  const names = useGeoObjectNames();
  const { activeProjectId: ctxActiveProjectId, setActiveProjectId } = useActiveProjectContext();
  const effectiveActiveProjectId = propActiveProjectId || ctxActiveProjectId || undefined;
  const [params, setParams] = useSearchParams();

  const statusParam = params.get('status');
  const status: GeoSchemeStatus | undefined =
    statusParam === 'all' ? undefined : isSchemeStatus(statusParam) ? statusParam : defaultStatus;
  const typeParam = params.get('type');
  const workType = isWorkType(typeParam) ? typeParam : undefined;
  const elementId = params.get('element_id') || undefined;
  const siteId = params.get('site_id') || undefined;
  const projectId = params.get('project_id') || params.get('project') || undefined;
  const page = Math.max(0, Number(params.get('page') ?? '0') || 0);

  useEffect(() => {
    if (projectId) {
      const projName = names.project(projectId);
      setActiveProjectId(projectId, projName ? { name: projName } : undefined);
    }
  }, [projectId, names, setActiveProjectId]);

  const sortedElements = useMemo(() => {
    const targetPid = projectId || effectiveActiveProjectId;
    if (!targetPid) return names.elementsList;
    return [...names.elementsList].sort((a, b) => {
      const aMatch = a.project_id === targetPid ? 0 : 1;
      const bMatch = b.project_id === targetPid ? 0 : 1;
      return aMatch - bMatch;
    });
  }, [names.elementsList, projectId, effectiveActiveProjectId]);

  const [q, setQ] = useState(params.get('q') ?? '');
  const debouncedQ = useDebouncedValue(q, 350);

  function patchParams(patch: Record<string, string | null>) {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null || v === '') next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: true });
  }

  useEffect(() => {
    const current = params.get('q') ?? '';
    if (debouncedQ.trim() === current) return;
    const next = new URLSearchParams(params);
    if (debouncedQ.trim()) next.set('q', debouncedQ.trim());
    else next.delete('q');
    next.delete('page');
    setParams(next, { replace: true });
    // params/setParams меняются при каждой навигации — синхронизируем только по вводу.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ]);

  const { data, isLoading, isFetching, error } = useListGeoSchemesQuery(
    {
      status,
      work_type: workType,
      project_id: projectId,
      site_id: siteId,
      element_id: elementId,
      q: params.get('q') ?? undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    },
    { pollingInterval: 30_000 },
  );

  const rows = data?.schemes ?? [];
  const total = data?.total ?? 0;
  const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min(total, (page + 1) * PAGE_SIZE);

  function rowTone(r: GeoSchemeListItem): 'danger' | 'warning' | undefined {
    if (audience === 'geodesist' && r.status === 'rejected') return 'danger';
    if (audience === 'engineer' && r.status === 'submitted') return 'warning';
    return undefined;
  }

  return (
    <div>
      <Toolbar>
        <FieldLabel>
          Поиск
          <Input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Номер, наименование, объект"
          />
        </FieldLabel>
        <FieldLabel>
          Статус
          <Select
            value={statusParam === 'all' ? 'all' : (status ?? 'all')}
            onChange={(e) => patchParams({ status: e.target.value, page: null })}
          >
            <option value="all">Все статусы</option>
            {SCHEME_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.value === 'rejected' && audience === 'geodesist'
                  ? 'Отклонена — требует корректировки'
                  : s.label}
              </option>
            ))}
          </Select>
        </FieldLabel>
        <FieldLabel>
          Тип работ
          <Select
            value={workType ?? ''}
            onChange={(e) => patchParams({ type: e.target.value || null, page: null })}
          >
            <option value="">Все</option>
            {WORK_TYPES.map((w) => (
              <option key={w.code} value={w.code}>
                {w.short}
              </option>
            ))}
          </Select>
        </FieldLabel>
        <FieldLabel>
          Конструктив
          <Select
            value={elementId ?? ''}
            onChange={(e) => patchParams({ element_id: e.target.value || null, page: null })}
          >
            <option value="">Все элементы</option>
            {sortedElements.map((el) => (
              <option key={el.id} value={el.id}>
                {el.code ? `${el.code} — ${el.title}` : el.title}
              </option>
            ))}
          </Select>
        </FieldLabel>
        {actions && <ActionsSlot>{actions}</ActionsSlot>}
      </Toolbar>

      {error ? (
        <ErrorText role="alert">
          {apiErrorMessage(error) ?? 'Не удалось загрузить реестр исполнительных схем.'}
        </ErrorText>
      ) : isLoading ? (
        <Muted>Загрузка реестра…</Muted>
      ) : rows.length === 0 ? (
        <Muted>{emptyText}</Muted>
      ) : (
        <>
          <TableWrap>
            <DataTable aria-busy={isFetching}>
              <thead>
                <tr>
                  <th>Номер</th>
                  <th>Наименование</th>
                  <th>Тип работ</th>
                  <th>Объект / конструктив</th>
                  <th>Статус</th>
                  <th className="num">Превышения</th>
                  <th className="num">Версия</th>
                  <th>Обновлена</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const href = `${linkBase}/${r.id}`;
                  const objectName = r.object_name || names.project(r.project_id) || '—';
                  const elementName = names.element(r.element_id);
                  const siteName = names.site(r.site_id);
                  return (
                    <ClickRow
                      key={r.id}
                      $tone={rowTone(r)}
                      onClick={(e) => {
                        if (r.project_id) {
                          setActiveProjectId(r.project_id, {
                            name: r.object_name || names.project(r.project_id),
                          });
                        }
                        if (e.target instanceof Element && e.target.closest('a,button')) return;
                        navigate(href);
                      }}
                    >
                      <td>
                        <NumberLink to={href}>{r.number}</NumberLink>
                      </td>
                      <td>
                        <TitleCell>{r.title}</TitleCell>
                        {audience === 'geodesist' && r.status === 'rejected' && (
                          <Attention>Требует корректировки</Attention>
                        )}
                      </td>
                      <td>{workTypeShort(r.work_type)}</td>
                      <td>
                        <ObjectCell>{objectName}</ObjectCell>
                        {(elementName || siteName) && (
                          <Reason>
                            {[siteName, elementName].filter(Boolean).join(' · ')}
                          </Reason>
                        )}
                      </td>
                      <td>
                        <SchemeStatusBadge status={r.status} rejectedBy={r.rejected_by} />
                        {r.status === 'rejected' && r.reject_reason && (
                          <Reason title={r.reject_reason}>{r.reject_reason}</Reason>
                        )}
                      </td>
                      <td className="num">
                        {r.exceeded_count == null ? (
                          '—'
                        ) : (
                          <Num $tone={r.exceeded_count > 0 ? 'danger' : undefined}>
                            {r.exceeded_count}
                            {r.measured_count != null ? ` из ${r.measured_count}` : ''}
                          </Num>
                        )}
                      </td>
                      <td className="num">
                        {r.current_version > 0 ? `v${r.current_version}` : '—'}
                      </td>
                      <td>{fmtDateTime(r.updated_at)}</td>
                    </ClickRow>
                  );
                })}
              </tbody>
            </DataTable>
          </TableWrap>
          <Pager>
            <span>
              {from}–{to} из {total}
            </span>
            {total > PAGE_SIZE && (
              <Row $gap={2}>
                <Button
                  type="button"
                  $variant="secondary"
                  $size="sm"
                  disabled={page === 0}
                  onClick={() => patchParams({ page: page - 1 > 0 ? String(page - 1) : null })}
                >
                  ← Назад
                </Button>
                <Button
                  type="button"
                  $variant="secondary"
                  $size="sm"
                  disabled={to >= total}
                  onClick={() => patchParams({ page: String(page + 1) })}
                >
                  Вперёд →
                </Button>
              </Row>
            )}
          </Pager>
        </>
      )}
    </div>
  );
}
